import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { RankHistoryPoint } from '../api/types';
import { count, shortDate } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, POSITION_COLOR } from './palette';

/**
 * Composition over time, so it is a stacked area rather than lines: the question
 * is what share of the catalogue sits in each position, and the total scanned
 * moves too. Colours are the same diverging scale the rest of the app uses for
 * position, so "blue is good" holds everywhere.
 */
export function RankHistoryArea({ points }: { points: RankHistoryPoint[] }) {
  if (points.length < 2) {
    return (
      <div className="state">
        <div>{points.length === 0 ? 'No scans yet.' : 'Only one week of history so far.'}</div>
        <div className="muted">A trend needs at least two weeks of scanning.</div>
      </div>
    );
  }

  const rows = points.map((point) => ({ ...point, label: shortDate(point.period) }));

  return (
    <>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={CHROME.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={{ stroke: CHROME.axis }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={40}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ stroke: CHROME.inkMuted, strokeWidth: 1 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as (typeof rows)[number];

              return (
                <ChartTooltip
                  title={`Week of ${row.label}`}
                  rows={[
                    { key: 'c', label: 'Cheapest', value: count(row.cheapest), color: POSITION_COLOR.cheapest },
                    {
                      key: 'm',
                      label: 'Top 3',
                      value: count(row.competitive),
                      color: POSITION_COLOR.competitive,
                    },
                    {
                      key: 'e',
                      label: 'Undercut',
                      value: count(row.expensive),
                      color: POSITION_COLOR.expensive,
                    },
                    { key: 't', label: 'Products scanned', value: count(row.products) },
                  ]}
                />
              );
            }}
          />
          <Area
            isAnimationActive={false}
            type="monotone"
            dataKey="cheapest"
            stackId="1"
            stroke={POSITION_COLOR.cheapest}
            fill={POSITION_COLOR.cheapest}
            fillOpacity={0.85}
          />
          <Area
            isAnimationActive={false}
            type="monotone"
            dataKey="competitive"
            stackId="1"
            stroke={POSITION_COLOR.competitive}
            fill={POSITION_COLOR.competitive}
            fillOpacity={0.85}
          />
          <Area
            isAnimationActive={false}
            type="monotone"
            dataKey="expensive"
            stackId="1"
            stroke={POSITION_COLOR.expensive}
            fill={POSITION_COLOR.expensive}
            fillOpacity={0.85}
          />
        </AreaChart>
      </ResponsiveContainer>

      <Legend
        entries={[
          { label: 'Cheapest', color: POSITION_COLOR.cheapest },
          { label: 'Top 3', color: POSITION_COLOR.competitive },
          { label: 'Undercut', color: POSITION_COLOR.expensive },
        ]}
      />
    </>
  );
}
